import React from 'react';
import { Typography } from '@mui/material';

import MassTextCampaign from '../../../../Model/MassTextCampaign';
import { DateFormat, timeStampToStr } from '../../../../utils/date';

import { EM_DASH } from '../utils/constants';

import StatusTag from './StatusTag';
import { Count, CampaignCell } from './CampaignTable/styles';

/**
 * Campaign-name column: the campaign/sequence name with its creation timestamp beneath.
 * @param {Object} columnTexts headers, e.g. texts.history.columns
 */
const nameColumn = (columnTexts) => ({
  field: 'name',
  headerName: columnTexts.campaign,
  flex: 1.5,
  renderCell: ({ row }) => (
    <CampaignCell>
      <Typography variant="body2">{row.name}</Typography>
      <Typography variant="caption" color="text.secondary">
        {timeStampToStr(row.createdAt, DateFormat.DATE_FORMAT_TIME_MINUTES) || EM_DASH}
      </Typography>
    </CampaignCell>
  ),
});

/**
 * Coach-name column, resolving the coach id against the provided coaches map.
 * @param {Object} columnTexts headers, e.g. texts.history.columns
 * @param {Object} coachesList map of coachId -> coach name
 */
const coachColumn = (columnTexts, coachesList) => ({
  field: 'coachName',
  headerName: columnTexts.coach,
  flex: 1,
  valueGetter: ({ row }) => coachesList[row.coachId] || EM_DASH,
});

/**
 * Builds the delivery-metric columns shared by every campaign table:
 * recipients, sent, failed and status. Each tab supplies its own leading
 * columns (campaign/sequence name, coach, etc.).
 *
 * @param {Object} columnTexts headers, e.g. texts.history.columns
 */
const metricColumns = (columnTexts) => [
  {
    field: 'recipients',
    headerName: columnTexts.recipients,
    flex: 0.8,
    valueGetter: ({ row }) => (row.totals ? row.totals.recipients : null),
    renderCell: ({ value }) => (value == null ? EM_DASH : value),
  },
  {
    field: 'sent',
    headerName: columnTexts.sent,
    flex: 0.6,
    // null while the campaign is still running: the server writes totals once, at close, so a 0
    // here would read as "none delivered" rather than "not counted yet".
    valueGetter: ({ row }) => (row.totals && !MassTextCampaign.isInFlight(row.status) ? row.totals.delivered : null),
    renderCell: ({ value }) => (value == null ? EM_DASH : <Count>{value}</Count>),
  },
  {
    field: 'failed',
    headerName: columnTexts.failed,
    flex: 0.6,
    valueGetter: ({ row }) => {
      if (!row.totals || MassTextCampaign.isInFlight(row.status)) return null;
      return MassTextCampaign.getFailedTotal(row.totals);
    },
    renderCell: ({ value }) => (value == null ? EM_DASH : <Count tone="fail">{value}</Count>),
  },
  {
    field: 'status',
    headerName: columnTexts.status,
    flex: 0.9,
    renderCell: ({ value }) => <StatusTag status={value} />,
  },
];

export default metricColumns;
export { nameColumn, coachColumn };
